import React, { useState, useEffect, useRef } from 'react';
import YouTube from 'react-youtube';
import { getFirestore, collection, query, where, limit, onSnapshot, addDoc, Timestamp, getDocs } from 'firebase/firestore';

const db = getFirestore();

const InteractiveVideo = ({ userEmail }) => {
  const [videoConfig, setVideoConfig] = useState(null);
  const [showVote, setShowVote] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]); 
  const [results, setResults] = useState({ counts: {}, total: 0 });
  const intervalRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, "videos"), where("isFeatured", "==", true), limit(1));
    return onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) setVideoConfig({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
    });
  }, []);

  useEffect(() => {
    if (!userEmail || !videoConfig?.videoId) return;
    const checkVote = async () => {
      const q = query(collection(db, "videoVotes"), where("videoId", "==", videoConfig.videoId), where("userEmail", "==", userEmail), limit(1));
      const snap = await getDocs(q);
      setHasVoted(!snap.empty);
    };
    checkVote();
  }, [videoConfig, userEmail]);

  useEffect(() => {
    if (!videoConfig?.videoId) return;
    const q = query(collection(db, "videoVotes"), where("videoId", "==", videoConfig.videoId));
    return onSnapshot(q, (snapshot) => {
      const counts = {};
      snapshot.forEach(d => { 
        const v = d.data().vote; 
        const choices = Array.isArray(v) ? v : [v];
        choices.forEach(c => { counts[c] = (counts[c] || 0) + 1; });
      });
      setResults({ counts, total: snapshot.size });
    });
  }, [videoConfig]);

  const toggleOption = (opt) => {
    setSelectedOptions(prev => prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]);
  };

  const handleFinalVote = async (choice) => {
    if (!userEmail) return;
    const finalChoice = choice || selectedOptions;
    if (Array.isArray(finalChoice) && finalChoice.length === 0) return alert("Select at least one!");

    await addDoc(collection(db, "videoVotes"), {
      vote: finalChoice,
      videoId: videoConfig.videoId,
      videoTitle: videoConfig.title,
      userEmail,
      timestamp: Timestamp.now()
    });
    setHasVoted(true);
  };

  if (!videoConfig) return null;
  const options = videoConfig.voteOptions || ['Yes', 'No'];

  // Youtube options to force the video to stay inside the app container
  const videoOptions = {
    width: '100%',
    height: '100%',
    playerVars: {
      autoplay: 0,
      modestbranding: 1,
      rel: 0,
      playsinline: 1, // Crucial: Prevents iOS/Android from opening the YT app
      origin: window.location.origin
    },
  };

  return (
    <div className="container-fluid px-2 px-md-3" style={{ maxWidth: '850px', margin: '0 auto', overflowX: 'hidden' }}>
      <style>{`
        .responsive-title { font-size: calc(1.1rem + 1vw); }
        .glass-card { background: rgb(3, 3, 3); border: 1px solid rgba(255, 255, 255, 0.15); }
        @media (max-width: 576px) {
          .vote-btn { width: 100% !important; margin-bottom: 8px; }
          .stats-text { font-size: 0.8rem !important; }
        }
      `}</style>

      <h2 className="text-center mb-3 mb-md-4 fw-bold teal-accent text-uppercase ls-wider responsive-title">
        {videoConfig.title}
      </h2>

      <div className="ratio ratio-16x9 mb-4 shadow-lg border border-secondary rounded overflow-hidden">
        <YouTube 
          videoId={videoConfig.videoId} 
          onStateChange={(e) => {
            if (e.data === 1) {
              intervalRef.current = setInterval(() => {
                const t = e.target.getCurrentTime();
                setShowVote(t >= videoConfig.voteStart && t <= videoConfig.voteEnd);
              }, 500);
            } else clearInterval(intervalRef.current);
          }} 
          opts={videoOptions} 
        />
      </div>

      {showVote && !hasVoted && (
        <div className="glass-card animate-fade-in p-3 p-md-4 text-center rounded shadow-lg border-info animated rollIn">
          <h4 className="teal-accent mb-3 fw-bold italic" style={{ fontSize: '1rem' }}>
            {videoConfig.isMulti ? 'CHOOSE MULTIPLE' : 'CAST YOUR VOTE'}
          </h4>
          
          {videoConfig.description && (
            <div className="bg-dark bg-opacity-50 p-3 rounded border border-secondary mb-3 smallest text-white-50 text-start" style={{ maxHeight: '100px', overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                <strong className="text-info text-uppercase d-block mb-1" style={{fontSize: '9px'}}>Details:</strong>
                {videoConfig.description}
            </div>
          )}

          <div className="d-flex justify-content-center flex-wrap gap-2 ">
            {options.map((opt, i) => (
              <button 
                key={opt} 
                className={`btn vote-btn px-4 py-2 fw-bold ${selectedOptions.includes(opt) || (i === 0 && !videoConfig.isMulti) ? 'active-save-vote' : ''}`} 
                onClick={() => videoConfig.isMulti ? toggleOption(opt) : handleFinalVote(opt)}
                style={!videoConfig.isMulti && i > 0 ? {borderColor: '#8f8e8e', color: '#6683ea'} : {borderColor: '#8f8e8e', color: '#6683ea'}}
              >
                {opt.toUpperCase()}
              </button>
            ))}
          </div>
          {videoConfig.isMulti && <button className="btn btn-success mt-3 w-100 fw-bold" onClick={() => handleFinalVote()}>SUBMIT VOTE</button>}
        </div>
      )}

      {(hasVoted || results.total > 0) && (
        <div className="glass-card p-3 p-md-4 mt-3 rounded shadow" style={{ width: '100%' }}>
          <h6 className="teal-accent text-uppercase smallest fw-bold mb-3">VOTE TOTALS</h6>
          {Object.entries(results.counts).sort((a,b) => b[1] - a[1]).map(([label, count]) => {
            const pct = results.total > 0 ? Math.round((count / results.total) * 100) : 0;
            return (
              <div key={label} className="mb-3">
                <div className="d-flex justify-content-between fw-bold text-white-50 stats-text">
                   <span className="text-truncate" style={{maxWidth: '60%'}}>{label.toUpperCase()}</span>
                   <span>{count} ({pct}%)</span>
                </div>
                <div className="progress-container mt-1" style={{ height: '10px', background: 'rgba(255,255,255,0.1)', borderRadius: '10px', overflow: 'hidden', width: '100%' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #12c2e9, #3a246d)', transition: '1s' }} />
                </div>
              </div>
            );
          })}
          <div className="text-center mt-2 orange stats-text" style={{fontSize: '0.9rem'}}>{results.total} participants</div>
        </div>
      )}
    </div>
  );
};

export default InteractiveVideo;